import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { ChevronDown, ChevronUp, Calendar, Loader2 } from 'lucide-react';
import CriteriaComparisonView from '../../../components/ui/CriteriaComparisonView';
import coverageApiService from '../../../services/coverage-api.service';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';
import './IndicationCriteriaCard.css';

/**
 * IndicationCriteriaCard - Collapsible card for a single indication's coverage criteria
 * Matches prototype structure exactly
 */
const IndicationCriteriaCard = ({
  indication,
  productId,
  formularyId,
  coverageStatus,
  cachedData,
  onCacheUpdate
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Get user info for analytics
  const userAttributes = useSelector((state) => state.auth.userAttributes);
  const searchParams = useSelector((state) => state.coverageVerification?.searchParams);
  
  // Use cached data if available
  const formularyStandardData = cachedData?.formularyStandard || null;
  const formularyClassData = cachedData?.formularyClass || null;
  const formularyStandardError = cachedData?.formularyStandardError || null;
  const formularyClassError = cachedData?.formularyClassError || null;
  const medicalStandardData = cachedData?.medicalStandard || null;
  const medicalClassData = cachedData?.medicalClass || null;
  const medicalStandardError = cachedData?.medicalStandardError || null;
  const medicalClassError = cachedData?.medicalClassError || null;
  const hasLoadedOnce = !!cachedData;

  const handleToggle = async () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    // Track indication expansion
    if (newExpanded) {
      try {
        sendToKinesis('coverage_events', 'INDICATION_CRITERIA_EXPANDED', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          planId: productId?.toString() || '',
          drugId: formularyId?.toString() || '',
          indicationId: indication.IndicationId?.toString() || '',
          indicationName: indication.name || '',
          hasCriteria: !!cachedData
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    }

    // Fetch data when expanding for the first time (if not cached)
    if (newExpanded && !cachedData) {
      console.log('[Cache] Cache miss - fetching indication criteria for:', indication.IndicationId);
      await fetchAllCriteriaData();
    } else if (newExpanded && cachedData) {
      console.log('[Cache] Cache hit - using cached indication criteria for:', indication.IndicationId);
    }
  };

  const fetchAllCriteriaData = async () => {
    setIsLoading(true);
    const indicationIdStr = indication.IndicationId.toString();
    const isNotCovered = coverageStatus === 'not_covered';
    
    // Debug logging
    console.log('IndicationCriteriaCard - Fetching criteria data:', {
      indicationId: indicationIdStr,
      coverageStatus,
      isNotCovered,
      willSkipFormularyAPIs: isNotCovered
    });

    try {
      let cacheData = {};
      
      if (isNotCovered) {
        // Drug is not covered - skip formulary API calls entirely
        // Set formulary data to null immediately
        cacheData.formularyStandard = null;
        cacheData.formularyClass = null;
        cacheData.formularyStandardError = null;
        cacheData.formularyClassError = null;

        // Only fetch medical criteria (2 API calls)
        const results = await Promise.allSettled([
          coverageApiService.getMedicalStandardCriteria(indicationIdStr, productId, formularyId),
          coverageApiService.getMedicalClassCriteria(indicationIdStr, productId, formularyId),
        ]);

        // Process medical results (indices 0 and 1)
        if (results[0].status === 'fulfilled') {
          cacheData.medicalStandard = results[0].value;
          cacheData.medicalStandardError = null;
        } else {
          cacheData.medicalStandard = null;
          cacheData.medicalStandardError = 'Failed to load medical standard criteria';
        }

        if (results[1].status === 'fulfilled') {
          cacheData.medicalClass = results[1].value;
          cacheData.medicalClassError = null;
        } else {
          cacheData.medicalClass = null;
          cacheData.medicalClassError = 'Failed to load medical class criteria';
        }
      } else {
        // Drug is covered - fetch all APIs in parallel (4 API calls)
        const results = await Promise.allSettled([
          coverageApiService.getFormularyStandardCriteria(indicationIdStr, productId, formularyId),
          coverageApiService.getFormularyClassCriteria(indicationIdStr, productId, formularyId),
          coverageApiService.getMedicalStandardCriteria(indicationIdStr, productId, formularyId),
          coverageApiService.getMedicalClassCriteria(indicationIdStr, productId, formularyId),
        ]);

        // Process formulary results (indices 0 and 1)
        if (results[0].status === 'fulfilled') {
          cacheData.formularyStandard = results[0].value;
          cacheData.formularyStandardError = null;
        } else {
          cacheData.formularyStandard = null;
          cacheData.formularyStandardError = 'Failed to load formulary standard criteria';
        }

        if (results[1].status === 'fulfilled') {
          cacheData.formularyClass = results[1].value;
          cacheData.formularyClassError = null;
        } else {
          cacheData.formularyClass = null;
          cacheData.formularyClassError = 'Failed to load formulary class criteria';
        }

        // Process medical results (indices 2 and 3)
        if (results[2].status === 'fulfilled') {
          cacheData.medicalStandard = results[2].value;
          cacheData.medicalStandardError = null;
        } else {
          cacheData.medicalStandard = null;
          cacheData.medicalStandardError = 'Failed to load medical standard criteria';
        }

        if (results[3].status === 'fulfilled') {
          cacheData.medicalClass = results[3].value;
          cacheData.medicalClassError = null;
        } else {
          cacheData.medicalClass = null;
          cacheData.medicalClassError = 'Failed to load medical class criteria';
        }
      }
      
      // Store in cache
      cacheData.loadedAt = Date.now();
      onCacheUpdate(cacheData);
      
      // Track MMIT API calls made for criteria
      const apiCallCount = isNotCovered ? 2 : 4; // 2 medical calls if not covered, 4 total if covered
      try {
        sendToKinesis('coverage_events', 'MMIT_API_CALLS_MADE', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          practice_id: userAttributes?.['custom:organization_id'] || '',
          session_id: window.currentSessionId || '',
          feature_module: 'coverage_criteria',
          mmit_api_call_count: apiCallCount,
          indicationId: indicationIdStr,
          indicationName: indication.name || '',
          planId: productId?.toString() || '',
          drugId: formularyId?.toString() || '',
          timestamp: new Date().toISOString()
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    } catch (error) {
      console.error('Error fetching criteria data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine coverage type badge (only show after data loads)
  const getCoverageTypeBadge = () => {
    if (!hasLoadedOnce || isLoading) return null;

    const formularyST = formularyStandardData?.result?.STDetail;
    const formularyPA = formularyStandardData?.result?.PADetail;
    const medicalST = medicalStandardData?.result?.STDetail;
    const medicalPA = medicalStandardData?.result?.PADetail;

    const hasST = 
      (formularyST && formularyST.StepStatus !== 'No Step' && formularyST.StepStatus !== 'Not Listed') ||
      (medicalST && medicalST.StepStatus !== 'No Step' && medicalST.StepStatus !== 'Not Listed');
    
    const hasPA = 
      formularyPA?.PAOverall === 'Restrictive' || 
      medicalPA?.PAOverall === 'Restrictive';

    if (hasST || hasPA) {
      return <Badge className="bg-status-restricted text-white border-0 text-[10px] sm:text-xs shrink-0">Restricted</Badge>;
    }

    return <Badge className="bg-status-covered text-white border-0 text-[10px] sm:text-xs shrink-0">No Restrictions Listed</Badge>;
  };

  const hasData = formularyStandardData || formularyClassData || medicalStandardData || medicalClassData;

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={handleToggle}
    >
      <Card className="border-2 shadow-sm">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4 sm:p-5 md:p-6">
            <div className="flex items-center justify-between gap-3 sm:gap-4">
              <div className="flex-1 space-y-2 sm:space-y-3 min-w-0">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <h3 className="text-sm sm:text-base font-semibold line-clamp-2">{indication.name}</h3>
                </div>
                {hasData && !isLoading && (
                  <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-normal text-muted-foreground">
                    <Calendar className="h-3 w-3 shrink-0" />
                    <span>Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 sm:pb-5 md:pb-6 px-4 sm:px-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-3 sm:px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary animate-spin mb-3 sm:mb-4" />
                <p className="text-xs sm:text-sm font-medium text-foreground mb-1">
                  Loading coverage criteria...
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground text-center max-w-md">
                  Fetching coverage criteria for {indication.name}
                </p>
              </div>
            ) : hasData ? (
              <div className="space-y-3 sm:space-y-4">
                <Tabs defaultValue="formulary" className="w-full">
                  <TabsList className="bg-muted flex-wrap h-auto gap-2 p-2 rounded-lg border shadow-sm">
                    <TabsTrigger value="formulary" className="text-[10px] sm:text-xs font-bold min-h-[44px] px-2 sm:px-3 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      <span className="hidden sm:inline">Pharmacy Benefit Criteria</span>
                      <span className="sm:hidden">Pharmacy</span>
                    </TabsTrigger>
                    <TabsTrigger value="medical" className="text-[10px] sm:text-xs font-bold min-h-[44px] px-2 sm:px-3 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      <span className="hidden sm:inline">Medical Benefit Criteria</span>
                      <span className="sm:hidden">Medical</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="formulary" className="mt-4">
                  <CriteriaComparisonView
                    type="formulary"
                    standardData={formularyStandardData}
                    classData={formularyClassData}
                    standardLoading={false}
                    classLoading={false}
                    standardError={formularyStandardError}
                    classError={formularyClassError}
                    notCovered={coverageStatus === 'not_covered'}
                  />
                </TabsContent>

                <TabsContent value="medical" className="mt-4">
                  <CriteriaComparisonView
                    type="medical"
                    standardData={medicalStandardData}
                    classData={medicalClassData}
                    standardLoading={false}
                    classLoading={false}
                    standardError={medicalStandardError}
                    classError={medicalClassError}
                  />
                </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4 bg-destructive/10 rounded-lg border-2 border-destructive/20">
                <p className="text-sm text-destructive">
                  Failed to load criteria data. Please try again.
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default IndicationCriteriaCard;
